export type TrickPlaySourceKind = 'sidecar-vtt' | 'embedded-hls' | 'none';

export type TrickPlayRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type TrickPlayFrame = {
  start: number;
  end: number;
  url: string;
  rect?: TrickPlayRect;
};

export type TrickPlayTrack = {
  kind: TrickPlaySourceKind;
  frames: TrickPlayFrame[];
};

export type TrickPlayChapter = {
  id: string;
  title: string;
  startTime: number;
  endTime: number;
};

export type PlaylistTrackLike = {
  file?: string;
  kind?: string;
};

export type PlaylistItemLike = {
  file?: string;
  sources?: Array<{ file?: string; type?: string }>;
  tracks?: PlaylistTrackLike[];
};

export type TrickPlayContext = {
  itemTracks?: PlaylistTrackLike[];
  playlistTracks?: PlaylistTrackLike[];
  sources?: Array<{ file?: string; type?: string }>;
  file?: string;
  fetchImpl?: typeof fetch;
};

export type TrickPlayProvider = {
  kind: Exclude<TrickPlaySourceKind, 'none'>;
  load: (context: TrickPlayContext) => Promise<TrickPlayFrame[]>;
};

type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Pick<Response, 'ok' | 'status' | 'text'>>;

const XYWH_PATTERN = /#xywh=(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i;
const HLS_IMAGE_CODECS = /(jpeg|jpg|png|webp|mjpeg)/i;
const HLS_VIDEO_CODECS = /(avc|hvc|hev|dvh|mp4a|ac-3|ec-3|vp9|av01)/i;

const parseTimestamp = (value: string) => {
  const normalized = value.trim().replace(',', '.');
  const parts = normalized.split(':');
  if (parts.length === 3) {
    return Number(parts[0]) * 3600 + Number(parts[1]) * 60 + Number(parts[2]);
  }
  if (parts.length === 2) {
    return Number(parts[0]) * 60 + Number(parts[1]);
  }
  return Number(normalized) || 0;
};

const resolveUrl = (value: string, base?: string) => {
  if (!value) {
    return value;
  }

  try {
    return new URL(value, base || (typeof window !== 'undefined' ? window.location.href : undefined)).href;
  } catch {
    return value;
  }
};

const parseAttributes = (line: string) => {
  const attributes: Record<string, string> = {};
  const payload = line.replace(/^#EXT-X-[^:]+:/i, '');
  const pattern = /([A-Z0-9-]+)=("([^"]*)"|([^,]*))/gi;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(payload))) {
    attributes[match[1].toUpperCase()] = match[3] ?? match[4] ?? '';
  }

  return attributes;
};

const parseResolution = (value?: string) => {
  if (!value) {
    return undefined;
  }

  const [width, height] = value.toLowerCase().split('x').map(Number);
  if (!width || !height) {
    return undefined;
  }

  return { width, height };
};

const parseXywh = (value: string): TrickPlayRect | undefined => {
  const match = value.match(XYWH_PATTERN);
  if (!match) {
    return undefined;
  }

  return {
    x: Number(match[1]),
    y: Number(match[2]),
    width: Number(match[3]),
    height: Number(match[4]),
  };
};

const isHlsUrl = (file?: string, type?: string) => {
  if (file && /\.m3u8($|\?)/i.test(file)) {
    return true;
  }

  const normalizedType = (type || '').toLowerCase();
  return normalizedType === 'hls' || normalizedType.includes('mpegurl');
};

export const isChapterSidecarFile = (file?: string, kind?: string) => {
  if (!file) {
    return false;
  }

  const normalizedKind = (kind || '').toLowerCase();
  if (normalizedKind === 'chapters' || normalizedKind === 'chapter') {
    return true;
  }

  if (
    normalizedKind === 'thumbnails' ||
    normalizedKind === 'thumbnail' ||
    normalizedKind === 'captions' ||
    normalizedKind === 'subtitles' ||
    normalizedKind === 'metadata'
  ) {
    return false;
  }

  return /chapters?/i.test(file) && /\.vtt($|\?)/i.test(file) && !/\/strips\/|thumbnails?|sprite/i.test(file);
};

export const isThumbnailSidecarFile = (file?: string, kind?: string) => {
  if (!file) {
    return false;
  }

  const normalizedKind = (kind || '').toLowerCase();
  if (normalizedKind === 'thumbnails' || normalizedKind === 'thumbnail') {
    return true;
  }

  if (normalizedKind === 'captions' || normalizedKind === 'subtitles' || normalizedKind === 'chapters') {
    return false;
  }

  return /\/strips\/|thumbnails?|sprite/i.test(file) && /\.vtt($|\?)/i.test(file);
};

export const collectThumbnailTrackSources = (...groups: Array<PlaylistTrackLike[] | undefined>) => {
  const seen = new Set<string>();
  const sources: PlaylistTrackLike[] = [];

  groups.forEach((group) => {
    group?.forEach((track) => {
      if (!isThumbnailSidecarFile(track.file, track.kind) || !track.file || seen.has(track.file)) {
        return;
      }

      seen.add(track.file);
      sources.push(track);
    });
  });

  return sources;
};

export const collectChapterTrackSources = (...groups: Array<PlaylistTrackLike[] | undefined>) => {
  const seen = new Set<string>();
  const sources: PlaylistTrackLike[] = [];

  groups.forEach((group) => {
    group?.forEach((track) => {
      if (!isChapterSidecarFile(track.file, track.kind) || !track.file || seen.has(track.file)) {
        return;
      }

      seen.add(track.file);
      sources.push(track);
    });
  });

  return sources;
};

export const collectHlsSourceUrls = (item?: PlaylistItemLike) => {
  const urls: string[] = [];
  const push = (file?: string, type?: string) => {
    if (!file || !isHlsUrl(file, type) || urls.includes(file)) {
      return;
    }

    urls.push(file);
  };

  item?.sources?.forEach((source) => push(source.file, source.type));
  push(item?.file);

  return urls;
};

export const parseChapterVtt = (vtt: string): TrickPlayChapter[] => {
  const chapters: TrickPlayChapter[] = [];
  const blocks = vtt.replace(/^\uFEFF/, '').split(/\r?\n\r?\n/);

  blocks.forEach((block, index) => {
    const lines = block.split(/\r?\n/).filter((line) => line.trim() !== '' && line.trim() !== 'WEBVTT' && !line.startsWith('NOTE'));
    const timeLineIndex = lines.findIndex((line) => line.includes('-->'));
    if (timeLineIndex < 0) {
      return;
    }

    const [startRaw, endRaw] = lines[timeLineIndex].split('-->');
    const startTime = parseTimestamp(startRaw.replace(/ .*$/, ''));
    const endTime = parseTimestamp(endRaw.trim().split(/[\s]/)[0]);
    const title = lines
      .slice(timeLineIndex + 1)
      .join(' ')
      .replace(/<[^>]+>/g, '')
      .trim();

    if (!title || XYWH_PATTERN.test(title) || /\.(jpe?g|png|webp)(#|\?|$)/i.test(title)) {
      return;
    }

    chapters.push({
      id: `chapter-${index}`,
      title,
      startTime,
      endTime,
    });
  });

  return chapters;
};

export const parseThumbnailVtt = (vtt: string, vttUrl?: string): TrickPlayFrame[] => {
  const frames: TrickPlayFrame[] = [];
  const blocks = vtt.replace(/^\uFEFF/, '').split(/\r?\n\r?\n/);

  blocks.forEach((block) => {
    const lines = block.split(/\r?\n/).filter((line) => line.trim() !== '' && line.trim() !== 'WEBVTT' && !line.startsWith('NOTE'));
    const timeLineIndex = lines.findIndex((line) => line.includes('-->'));
    if (timeLineIndex < 0) {
      return;
    }

    const [startRaw, endRaw] = lines[timeLineIndex].split('-->');
    const start = parseTimestamp(startRaw.replace(/ .*$/, ''));
    const end = parseTimestamp(endRaw.trim().split(/[\s]/)[0]);
    const text = lines.slice(timeLineIndex + 1).join('').trim();
    if (!text) {
      return;
    }

    const rect = parseXywh(text);
    const rawUrl = text.replace(XYWH_PATTERN, '').trim();
    if (!rawUrl) {
      return;
    }

    frames.push({
      start,
      end,
      url: resolveUrl(rawUrl, vttUrl),
      rect,
    });
  });

  return frames;
};

const isImageCodec = (codecs?: string) => {
  if (!codecs) {
    return true;
  }

  if (HLS_IMAGE_CODECS.test(codecs)) {
    return true;
  }

  return !HLS_VIDEO_CODECS.test(codecs);
};

const isImageSegment = (uri: string) => /\.(jpe?g|png|webp|gif)($|\?)/i.test(uri);
const isVideoSegment = (uri: string) => /\.(ts|m4s|cmfv)($|\?)/i.test(uri) || (/\.mp4($|\?)/i.test(uri) && !isImageSegment(uri));
const isHlsPlaylistUri = (uri: string) => /\.m3u8($|\?)/i.test(uri);
const isMasterPlaylist = (manifest: string) =>
  /#EXT-X-(STREAM-INF|IMAGE-STREAM-INF|I-FRAME-STREAM-INF):/i.test(manifest);

export type HlsImageStreamCandidate = {
  uri: string;
  bandwidth?: number;
  width?: number;
  height?: number;
};

export const parseHlsMasterImageUris = (manifest: string, manifestUrl?: string): HlsImageStreamCandidate[] => {
  const candidates: HlsImageStreamCandidate[] = [];

  manifest.split(/\r?\n/).forEach((rawLine) => {
    const line = rawLine.trim();
    if (!line.startsWith('#EXT-X-')) {
      return;
    }

    const isImageStream = /^#EXT-X-IMAGE-STREAM-INF:/i.test(line);
    const isMedia = /^#EXT-X-MEDIA:/i.test(line);
    if (!isImageStream && !isMedia) {
      return;
    }

    const attributes = parseAttributes(line);
    const uri = attributes.URI;
    if (!uri) {
      return;
    }

    if (isMedia) {
      const type = (attributes.TYPE || '').toUpperCase();
      const group = `${attributes['GROUP-ID'] || ''} ${attributes.NAME || ''}`.toLowerCase();
      const looksLikeThumbnails = /thumb|trick|image|sprite/.test(group);
      if (type === 'SUBTITLES' || type === 'AUDIO' || type === 'CLOSED-CAPTIONS') {
        return;
      }
      if (type !== 'VIDEO' || !looksLikeThumbnails) {
        return;
      }
      if (!isImageCodec(attributes.CODECS)) {
        return;
      }
    } else if (!isImageCodec(attributes.CODECS)) {
      return;
    }

    const resolution = parseResolution(attributes.RESOLUTION);
    candidates.push({
      uri: resolveUrl(uri, manifestUrl),
      bandwidth: attributes.BANDWIDTH ? Number(attributes.BANDWIDTH) : undefined,
      width: resolution?.width,
      height: resolution?.height,
    });
  });

  return candidates.sort((a, b) => {
    const aDelta = Math.abs((a.width || 160) - 160);
    const bDelta = Math.abs((b.width || 160) - 160);
    if (aDelta !== bDelta) {
      return aDelta - bDelta;
    }

    return (a.bandwidth || 0) - (b.bandwidth || 0);
  });
};

const parseTiles = (attributes: Record<string, string>, fallbackDuration: number) => {
  const resolution = parseResolution(attributes.RESOLUTION);
  const layout = (attributes.LAYOUT || '').toLowerCase().split('x').map(Number);
  const columns = layout[0] || 1;
  const rows = layout[1] || 1;
  const tileDuration = Number(attributes.DURATION) || fallbackDuration / Math.max(1, columns * rows);

  return {
    columns,
    rows,
    tileDuration,
    tileWidth: resolution?.width,
    tileHeight: resolution?.height,
  };
};

export const parseHlsImagePlaylist = (manifest: string, playlistUrl?: string): TrickPlayFrame[] => {
  const frames: TrickPlayFrame[] = [];
  const lines = manifest.split(/\r?\n/).map((line) => line.trim());
  let segmentDuration = 0;
  let tiles: ReturnType<typeof parseTiles> | undefined;
  let time = 0;
  let imageSegments = 0;
  let videoSegments = 0;

  const pushImage = (uri: string) => {
    const url = resolveUrl(uri, playlistUrl);
    if (tiles) {
      const tileWidth = tiles.tileWidth || 0;
      const tileHeight = tiles.tileHeight || 0;
      for (let row = 0; row < tiles.rows; row += 1) {
        for (let column = 0; column < tiles.columns; column += 1) {
          frames.push({
            start: time,
            end: time + tiles.tileDuration,
            url,
            rect:
              tileWidth && tileHeight
                ? {
                    x: column * tileWidth,
                    y: row * tileHeight,
                    width: tileWidth,
                    height: tileHeight,
                  }
                : undefined,
          });
          time += tiles.tileDuration;
        }
      }
      return;
    }

    frames.push({
      start: time,
      end: time + (segmentDuration || 0),
      url,
    });
    time += segmentDuration;
  };

  lines.forEach((line) => {
    if (!line || line.startsWith('#EXTM3U')) {
      return;
    }

    if (/^#EXTINF:/i.test(line)) {
      segmentDuration = Number(line.replace(/^#EXTINF:/i, '').split(',')[0]) || 0;
      return;
    }

    if (/^#EXT-X-TILES:/i.test(line)) {
      tiles = parseTiles(parseAttributes(line), segmentDuration);
      return;
    }

    if (line.startsWith('#')) {
      return;
    }

    if (isHlsPlaylistUri(line) || (isVideoSegment(line) && !isImageSegment(line))) {
      videoSegments += 1;
      tiles = undefined;
      segmentDuration = 0;
      return;
    }

    imageSegments += 1;
    pushImage(line);
    tiles = undefined;
    segmentDuration = 0;
  });

  if (videoSegments && !imageSegments) {
    return [];
  }

  return frames;
};

const fetchText = async (url: string, fetchImpl: FetchLike) => {
  const response = await fetchImpl(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch ${url} (${response.status})`);
  }

  return response.text();
};

export const loadSidecarThumbnailFrames = async (context: TrickPlayContext): Promise<TrickPlayFrame[]> => {
  const fetchImpl = (context.fetchImpl || fetch) as FetchLike;
  const sources = collectThumbnailTrackSources(context.itemTracks, context.playlistTracks);

  for (const source of sources) {
    if (!source.file) {
      continue;
    }

    try {
      const vtt = await fetchText(source.file, fetchImpl);
      const frames = parseThumbnailVtt(vtt, source.file);
      if (frames.length) {
        return frames;
      }
    } catch {
      // Try the next sidecar if this VTT is missing or blocked.
    }
  }

  return [];
};

export const loadChapters = async (context: TrickPlayContext): Promise<TrickPlayChapter[]> => {
  const fetchImpl = (context.fetchImpl || fetch) as FetchLike;
  const sources = collectChapterTrackSources(context.itemTracks, context.playlistTracks);

  for (const source of sources) {
    if (!source.file) {
      continue;
    }

    try {
      const vtt = await fetchText(source.file, fetchImpl);
      const chapters = parseChapterVtt(vtt);
      if (chapters.length) {
        return chapters;
      }
    } catch {
      // Try the next chapter file if this VTT is missing or blocked.
    }
  }

  return [];
};

export const loadEmbeddedHlsThumbnailFrames = async (context: TrickPlayContext): Promise<TrickPlayFrame[]> => {
  const fetchImpl = (context.fetchImpl || fetch) as FetchLike;
  const masterUrls = collectHlsSourceUrls({
    file: context.file,
    sources: context.sources,
  });

  for (const masterUrl of masterUrls) {
    try {
      const manifest = await fetchText(masterUrl, fetchImpl);
      if (!isMasterPlaylist(manifest)) {
        const directFrames = parseHlsImagePlaylist(manifest, masterUrl);
        if (directFrames.length) {
          return directFrames;
        }
      }

      const candidates = parseHlsMasterImageUris(manifest, masterUrl);
      for (const candidate of candidates) {
        try {
          const playlist = await fetchText(candidate.uri, fetchImpl);
          const frames = parseHlsImagePlaylist(playlist, candidate.uri);
          if (frames.length) {
            return frames;
          }
        } catch {
          // Try the next image playlist advertised by the master.
        }
      }
    } catch {
      // Master playlist may be CORS-blocked or unavailable.
    }
  }

  return [];
};

export const sidecarVttTrickPlayProvider: TrickPlayProvider = {
  kind: 'sidecar-vtt',
  load: loadSidecarThumbnailFrames,
};

export const embeddedHlsTrickPlayProvider: TrickPlayProvider = {
  kind: 'embedded-hls',
  load: loadEmbeddedHlsThumbnailFrames,
};

export const defaultTrickPlayProviders: TrickPlayProvider[] = [sidecarVttTrickPlayProvider, embeddedHlsTrickPlayProvider];

export const loadTrickPlayTrack = async (
  context: TrickPlayContext,
  providers: TrickPlayProvider[] = defaultTrickPlayProviders,
): Promise<TrickPlayTrack> => {
  for (const provider of providers) {
    const frames = await provider.load(context);
    if (frames.length) {
      return { kind: provider.kind, frames };
    }
  }

  return { kind: 'none', frames: [] };
};

export const getTrickPlayFrame = (frames: TrickPlayFrame[], time: number): TrickPlayFrame | undefined => {
  if (!frames.length || !Number.isFinite(time)) {
    return undefined;
  }

  const match = frames.find((frame) => time >= frame.start && time < frame.end);
  if (match) {
    return match;
  }

  if (time < frames[0].start) {
    return frames[0];
  }

  return frames[frames.length - 1];
};

export const getChapterAtTime = (chapters: TrickPlayChapter[], time: number): TrickPlayChapter | undefined => {
  if (!chapters.length || !Number.isFinite(time)) {
    return undefined;
  }

  const ranged = chapters.find((chapter) => chapter.endTime > chapter.startTime && time >= chapter.startTime && time < chapter.endTime);
  if (ranged) {
    return ranged;
  }

  return [...chapters].reverse().find((chapter) => time >= chapter.startTime);
};

export const preloadTrickPlayImages = (frames: TrickPlayFrame[], limit = 4) => {
  if (typeof Image === 'undefined') {
    return;
  }

  const urls = Array.from(new Set(frames.map((frame) => frame.url))).slice(0, limit);
  urls.forEach((url) => {
    const image = new Image();
    image.src = url;
  });
};
